import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { EditConnectionActionDialogBody } from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogBody';
import { CredentialOption, EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { RemoteConnectionDetailInfo } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { DataConnections, WifAuthMode } from '~src/datasource/dataConnection/resources/DataConnections';
import { ICheckConnectionResult } from '~src/datasource/dataConnection/serverApi/CheckConnectionRequest';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

// W-22483230: static per-row presentation for one connection in the multi-row Edit Connection
// dialog. Field visibility is connector-aware: a class that does not use a field hides that row
// rather than showing an inapplicable empty input. WIF picker visibility is per row by class.
export interface EditConnectionRowProps {
    connectionId: string;
    rowLabel: string;
    isServerNameVisible: boolean;
    isServerPortVisible: boolean;
    isServerNameDisabled: boolean;
    isServerPortDisabled: boolean;
    isUsernameVisible: boolean;
    isEmbeddedToggleVisible: boolean;
    isTestConnectionButtonVisible: boolean;
    showNetworkType: boolean;
    // wifModesAvailable holds the WIF modes offered for the class; empty => the auth-mode row is hidden.
    isWifSupported: boolean;
    wifModesAvailable: WifAuthMode[];
    // W-22483245: chooser applicability for this row's class. isOAuthEmbeddedApplicable gates the
    // OAuth-embedded (account picker) radio; isStoredWif is captured once from the stored connection
    // at dialog-open so the OAuth-embedded and No-auth radios can be disabled for a connection that
    // is already WIF (switching a WIF connection to another auth mode is not supported yet).
    isOAuthEmbeddedApplicable: boolean;
    isStoredWif: boolean;
    // Static per connection: whether the embedded OAuth credential is a managed keychain. Drives the
    // read-only account dropdown plus the warning that the managed-keychain option is lost on change.
    usingManagedKeychain: boolean;
    addNewOAuthCredentialOptionValue: string | undefined;
    currentConnectionTypeDisplayName: string;
    // The connection this row edits, used by the add-account flow to reload its credential list.
    connection: EditableConnection;
    onTestConnection: (rowState: EditConnectionDialogState) => Promise<ICheckConnectionResult>;
}

// W-22483230: mutable field values for one row. Carries connectionId so updates and the apply path
// target the right row by id rather than by array position.
export interface PerConnectionFieldState {
    connectionId: string;
    serverName: string | undefined;
    serverPort: string | undefined;
    username: string | undefined;
    isPasswordEmbedded: boolean;
    newPassword: string | undefined;
    usingRemoteQueryAgent: boolean | undefined;
    // workgroupAuthMode is the WIF wire value, or undefined when the connection is not WIF / WIF is
    // not offered. authFederationServiceAccount applies only to impersonation.
    workgroupAuthMode: WifAuthMode | undefined;
    authFederationServiceAccount: string | undefined;
    // W-22483245: OAuth credential model driving the account picker and the OAuth save branch. The
    // selected auth radio is derived from workgroupAuthMode + credentialOption (single source of
    // truth), not stored separately. credentialId is the selected OAuth account (undefined for a
    // managed keychain the user can see but not edit).
    credentialOption: CredentialOption;
    credentialId: string | undefined;
    credentials: IOAuthCredential[];
    credentialNoValueOptionText: string | undefined;
}

// One row's field values minus its id; a stable export so the apply-path mapping and existing tests
// keep operating on a single row.
export type EditConnectionDialogState = Omit<PerConnectionFieldState, 'connectionId'>;

// W-22483230: one row per selected connection; props and state are ordered identically and keyed by
// connectionId.
export interface EditConnectionDialogProps {
    prompt: string;
    rows: EditConnectionRowProps[];
}

export interface EditConnectionMultiRowState {
    rows: PerConnectionFieldState[];
}

export interface StateAndProps {
    editConnectionDialogProps: EditConnectionDialogProps;
    editConnectionDialogState: EditConnectionMultiRowState;
}

export let PasswordUnchanged: string = null;
const GENERIC_JDBC_CONNECTION_TYPE = 'genericjdbc';
export namespace EditConnectionAction {

    export async function confirmEditDatabaseConnections(
        connections: EditableConnection[]): Promise<Promise<any>> {
        const isMultiSelection = connections.length > 1;
        const { editConnectionDialogProps, editConnectionDialogState } = await createDialogStateAndProps(connections, isMultiSelection);

        const modalInstance = openReactActionDialog<EditConnectionDialogProps, EditConnectionMultiRowState, IResult>({
            title: Messages.editConnection(),
            bodyComponent: EditConnectionActionDialogBody,
            initialBodyProps: editConnectionDialogProps,
            initialDialogState: editConnectionDialogState,
            actionName: Messages.save(),
            action: (props, dialogState) => isMultiSelection
                ? DataConnections.updateConnections(
                    connections.map((connection) => connection.id),
                    EditConnectionReactActionUtils.editConnectionDialogStateToConnectionUpdate(dialogState.rows[0]))
                  .catch((reason: any): IResult => (reason?.errors?.length ? reason : { errors: [{ code: reason }] }))
                  .then((result: IResult) => {
                      EditConnectionReactActionUtils.notifyResult(result, connections);
                      return result;
                  })
                : EditConnectionReactActionUtils.applyConnectionEdits(props.rows, dialogState.rows),
            dialogWidth: TabDialogWidth.width375px,
            dismissDialogOnActionFailed: true,
            // Save is disabled if any row is invalid (bad port or an inconsistent WIF state).
            actionDisabled: (_bodyProps: EditConnectionDialogProps, dialogState: EditConnectionMultiRowState) =>
                dialogState.rows.some((rowState) =>
                    !EditConnectionReactActionUtils.isPortValid(rowState.serverPort)
                    || !EditConnectionReactActionUtils.isWifStateValid(rowState))
        });

        return modalInstance.result;
    }

    async function createDialogStateAndProps(
        connections: EditableConnection[],
        isMultiSelection: boolean
    ): Promise<StateAndProps> {
        const canSessionUserEditEmbeddedCredentials: boolean = getSessionManagementServiceInstance().canSessionUserEditEmbeddedCredentials();
        const isOnlineEnvironment: boolean = getSessionManagementServiceInstance().isOnlineEnvironment();

        // canEmbedPassword (published non-cube) is per-datasource; a multi-select can span different
        // datasources (standalone vs. workbook-embedded, non-cube vs. published cube), so resolve it
        // from each connection's own datasource rather than reusing connections[0]'s answer.
        const canEmbedPasswordPerConnection = await Promise.all(connections.map((connection) =>
            EditConnectionReactActionUtils.canEmbedPassword(
                ((connection as IDataConnectionInfo).datasource) as IDataSourceInfo)));

        const rowProps: EditConnectionRowProps[] = [];
        const rowState: PerConnectionFieldState[] = [];

        connections.slice(0, 1).forEach((connection, index) => {
            // Username and the embedded-password toggle additionally require the embedded-credential
            // permission gate on top of class-level field applicability.
            const fieldApplicability = EditConnectionReactActionUtils.connectionFieldApplicability(connection.connectionType);
            // Several connections: a port the user cannot retype (hidden by class, or a disabled genericjdbc port) seeds unset, not as a Save-locking Mixed.
            const isPortEditable = fieldApplicability.serverPort && connection.connectionType !== GENERIC_JDBC_CONNECTION_TYPE;

            // W-23547170: the OAuth<->WIF chooser now lives in the OAuth-specific dialog, so a
            // WIF-eligible connection never routes here. The chooser row-props and WIF row-state fields
            // stay on the shared EditConnectionRowProps/PerConnectionFieldState shapes the OAuth dialog
            // also uses, so this plain-field editor defaults them to falsy rather than deriving them.
            // With isWifSupported/isOAuthEmbeddedApplicable false, applyRowEdit's chooser branch never
            // fires for a unified row -- it takes only the plain updateConnections path.
            rowProps.push({
                connectionId: connection.id,
                rowLabel: isMultiSelection ? '' : EditConnectionReactActionUtils.rowLabel(connection, index),
                isServerNameVisible: fieldApplicability.serverName,
                isServerPortVisible: fieldApplicability.serverPort,
                isServerNameDisabled: connection.connectionType === GENERIC_JDBC_CONNECTION_TYPE,
                isServerPortDisabled: connection.connectionType === GENERIC_JDBC_CONNECTION_TYPE,
                isUsernameVisible: canSessionUserEditEmbeddedCredentials && fieldApplicability.username,
                isEmbeddedToggleVisible: canSessionUserEditEmbeddedCredentials && canEmbedPasswordPerConnection.every(Boolean) && fieldApplicability.embeddedPassword,
                isTestConnectionButtonVisible: !isMultiSelection && connection.testingConnectionAllowed,
                // showNetworkType (embedded/flow) is per-datasource and synchronous -- compute it here
                // from this connection's own datasource rather than reusing connections[0]'s answer.
                showNetworkType: isOnlineEnvironment
                    && EditConnectionReactActionUtils.showNetworkType(
                        ((connection as IDataConnectionInfo).datasource) as IDataSourceInfo),
                isWifSupported: false,
                wifModesAvailable: [],
                isOAuthEmbeddedApplicable: false,
                isStoredWif: false,
                usingManagedKeychain: false,
                addNewOAuthCredentialOptionValue: undefined,
                currentConnectionTypeDisplayName: connection.connectionTypeDisplayName,
                connection: connection,
                onTestConnection: (state: EditConnectionDialogState) =>
                    EditConnectionReactActionUtils.checkEditedConnection(connection.id, state)
            });

            rowState.push({
                connectionId: connection.id,
                serverName: EditConnectionReactActionUtils.uniqueValueOrMixed(connections, (c) => (c.connectionDetails as RemoteConnectionDetailInfo).serverName),
                serverPort: isMultiSelection && !isPortEditable ? undefined
                    : EditConnectionReactActionUtils.uniqueValueOrMixed(connections, (c) => (c.connectionDetails as RemoteConnectionDetailInfo).serverPort),
                username: EditConnectionReactActionUtils.uniqueValueOrMixed(connections, (c) => (c.connectionDetails as RemoteConnectionDetailInfo).databaseUsername),
                isPasswordEmbedded: EditConnectionReactActionUtils.uniqueValueOrUndefined(connections, (c) => (c.connectionDetails as RemoteConnectionDetailInfo).hasEmbeddedPassword),
                newPassword: PasswordUnchanged,
                usingRemoteQueryAgent: connection.usingRemoteQueryAgent,
                workgroupAuthMode: undefined,
                authFederationServiceAccount: undefined,
                credentialOption: 'none',
                credentialId: undefined,
                credentials: [],
                credentialNoValueOptionText: undefined
            });
        });

        const editConnectionDialogProps: EditConnectionDialogProps = {
            prompt: EditConnectionReactActionUtils.makePrompt(connections.length),
            rows: rowProps
        };

        return { editConnectionDialogProps, editConnectionDialogState: { rows: rowState } };
    }

}
